import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { LoggerService } from '../../../infra/logger/logger.service';
import { HttpService } from '../../http/http.service';
import { AgentStepModel } from 'src/app/models/api/agentStep.model';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { map } from 'rxjs/operators';
import { AgentStepAttachmentDownloadResponse } from './types';

@Injectable({
  providedIn: 'root',
})
export class AiAgentStepsHttpService {
  public readonly defaultPageSize: number = 50;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _logger: LoggerService
  ) {}

  /**
   * @public Get single inventory resource by id
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public getAiAgentStep(integrationId: string, resourceId: string): Observable<AgentStepModel> {
    const url = `detection/integrations/${integrationId}/agentSteps/${resourceId}`;
    return this._httpService.get<AgentStepModel>(url, undefined, undefined, true);
  }

  /**
   * @public Start getting first page of inventory resources
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public startGetAgentSteps(
    filterOptions: ISelectedFilters,
    sortData?: SortColumn
  ): Observable<JsonHalCollection<AgentStepModel>> {
    const url = 'detection/agentSteps';
    const httpParams = this._createAgentStepsQueryParams(filterOptions, this.defaultPageSize, sortData);
    return this._httpService.get<JsonHalCollection<AgentStepModel>>(url, undefined, httpParams, true);
  }

  /**
   * @public Continue getting inventory resources using a continuation token
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public getAgentStepsByContinuationToken(continuationToken: string): Observable<JsonHalCollection<AgentStepModel>> {
    const url = 'detection/agentSteps-pages';
    const httpParams = new HttpParams().append('continuationToken', continuationToken);
    return this._httpService.get<JsonHalCollection<AgentStepModel>>(url, undefined, httpParams, true);
  }

  public getAgentStepSensitiveData(
    integrationId: string,
    agentStepId: string,
    action: SensitiveDataRequestActions,
    sourceId?: string,
    findingId?: string
  ): Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    const url = `detection/integrations/${integrationId}/agentSteps/${agentStepId}/${action}:execute`;
    const body = findingId ? { findingId } : undefined;
    if (action === SensitiveDataRequestActions.snippet && sourceId) {
      return this._httpService
        .post<typeof body, ActionGetAgentStepSensitiveDataResponseModel>(url, body, undefined, undefined, true)
        .pipe(
          map((response) => {
            if (response.snippet) {
              if (sourceId in response.snippet) {
                response.singleSnippet = response.snippet[sourceId];
              } else {
                response.singleSnippet = 'Snippet not found';
              }
            }
            return response;
          })
        );
    } else {
      return this._httpService.post<typeof body, ActionGetAgentStepSensitiveDataResponseModel>(
        url,
        body,
        undefined,
        undefined,
        true
      );
    }
  }

  /**
   * @public Get single inventory autocomplete options
   * @returns an Observable{AutocompleteSearchResultsModel} with the displayNames
   */
  public getAutocompleteOptions(query: string, filterName: string): Observable<AutocompleteSearchResultsV2Model[]> {
    const supportedFilterNames = ['actor', 'agent', 'environment', 'toolOrigin'];
    const mappedFilterName: Record<string, string> = {
      actor: 'mainActorDisplayName',
      agent: 'aiAgentDisplayName',
      environment: 'environment',
      toolOrigin: 'toolOriginLabel',
    };

    if (!supportedFilterNames.includes(filterName)) {
      return of([]);
    }

    const url = `detection/autocomplete/agentSteps`;
    const mapped = mappedFilterName[filterName];
    const httpParams = this._createAgentStepsQueryParams(
      { query, filter_name: mapped },
      this.defaultPageSize,
      undefined,
      false
    );

    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, httpParams, true);
  }

  /**
   * @public Get display name by id
   * @returns an Observable{AutocompleteSearchResultsV2Model} with the display names
   */
  public getDisplayNameById(filter_name: string, filter_id: string[]): Observable<AutocompleteSearchResultsV2Model[]> {
    const url = `detection/autocomplete/agentSteps/options`;
    const httpParams = this._createAgentStepsQueryParams(
      { filter_id: filter_id, filter_name: filter_name },
      this.defaultPageSize,
      undefined,
      false
    );
    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, httpParams, true);
  }

  /**
   * @public Get available client applications based on active integrations
   * @returns Observable array of client application strings
   */
  public getAvailableClientApplications(): Observable<string[]> {
    const url = 'detection/agentSteps/clientApplications';
    return this._httpService.get<string[]>(url, undefined, undefined, true);
  }

  public deleteAiAgentStepAttachment(fileId: string, integrationId: string, agentStepId: string): Observable<void> {
    const url = `detection/integrations/${integrationId}/agentSteps/${agentStepId}/actions/chatGptEnterpriseDeleteAttachmentAction:execute`;
    return this._httpService.post<{ fileId: string }, void>(url, { fileId }, undefined, undefined, true);
  }

  public downloadAiAgentStepAttachment(
    fileId: string,
    fileName: string,
    integrationId: string,
    agentStepId: string
  ): Observable<AgentStepAttachmentDownloadResponse> {
    const url = `detection/integrations/${integrationId}/agentSteps/${agentStepId}/actions/chatGptEnterpriseDownloadAttachmentAction:execute`;
    return this._httpService.post<{ fileId: string; fileName: string }, AgentStepAttachmentDownloadResponse>(
      url,
      {
        fileId,
        fileName,
      },
      undefined,
      undefined,
      true
    );
  }

  private _createAgentStepsQueryParams(
    filterOptions: ISelectedFilters,
    pageSize: number,
    sortData?: SortColumn,
    shouldLimitResultsAmount = true
  ): HttpParams {
    let httpParams = new HttpParams();
    if (shouldLimitResultsAmount) {
      httpParams = httpParams.append('pageSize', pageSize);
    }
    httpParams = this._appendAgentStepsFilterQueryParams(httpParams, filterOptions);

    if (sortData) {
      httpParams = this._appendInventorySortQueryParams(httpParams, sortData);
    }
    return httpParams;
  }

  private _appendAgentStepsFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
    if (filterOptions.resourceId && filterOptions.resourceId.length > 0) {
      httpParams = httpParams.append('id', filterOptions.resourceId.join(','));
    }

    if (filterOptions.integration && filterOptions.integration.length > 0) {
      httpParams = httpParams.append('integrationId', filterOptions.integration.join(','));
    }

    if (filterOptions.type && filterOptions.type.length > 0) {
      httpParams = httpParams.append('stepType', filterOptions.type.join(','));
    }

    if (filterOptions.actor && filterOptions.actor.length > 0) {
      httpParams = httpParams.append('mainActorId', filterOptions.actor.join(','));
    }

    if (filterOptions.fromDate) {
      httpParams = httpParams.append('sinceTimestamp', filterOptions.fromDate.toISOString());
    }

    if (filterOptions.toDate) {
      httpParams = httpParams.append('untilTimestamp', filterOptions.toDate.toISOString());
    }

    if (filterOptions.threadId) {
      httpParams = httpParams.append('threadId', filterOptions.threadId.join(','));
    }

    if (filterOptions.aiService && filterOptions.aiService.length > 0) {
      httpParams = httpParams.append('aiService', filterOptions.aiService.join(','));
    }

    if (filterOptions.clientApplication && filterOptions.clientApplication.length > 0) {
      httpParams = httpParams.append('clientApplication', filterOptions.clientApplication.join(','));
    }

    if (filterOptions.filter_name && filterOptions.filter_name.length > 0) {
      httpParams = httpParams.append('filterName', filterOptions.filter_name);
    }

    if (filterOptions.filter_id && filterOptions.filter_id.length > 0) {
      httpParams = httpParams.append('filterId', `${filterOptions.filter_id}`);
    }

    if (filterOptions.query) {
      httpParams = httpParams.append('query', filterOptions.query);
    }

    if (filterOptions.agent) {
      httpParams = httpParams.append('aiAgentId', filterOptions.agent.join(','));
    }

    if (filterOptions.environment) {
      httpParams = httpParams.append('environmentId', filterOptions.environment.join(','));
    }

    if (filterOptions.chatMode && filterOptions.chatMode.length > 0) {
      httpParams = httpParams.append('chatMode', filterOptions.chatMode.join(','));
    }

    if (filterOptions.toolOrigin && filterOptions.toolOrigin.length > 0) {
      httpParams = httpParams.append('toolsOrigins', filterOptions.toolOrigin.join(','));
    }

    if (filterOptions.dataItem && filterOptions.dataItem.length > 0) {
      httpParams = httpParams.append('dataItemId', filterOptions.dataItem.join(','));
    }

    if (filterOptions.source_pipeline && filterOptions.source_pipeline.length > 0) {
      httpParams = httpParams.append('sourcePipeline', filterOptions.source_pipeline.join(','));
    }

    return httpParams;
  }

  private _appendInventorySortQueryParams(httpParams: HttpParams, sortData: SortColumn): HttpParams {
    const sortFieldString: string | undefined = sortData.column.sortKey;
    const ascOrDescString = sortData.isDesc ? 'desc' : 'asc';

    if (sortFieldString) {
      httpParams = httpParams.append('sortBy', `[${sortFieldString}(${ascOrDescString})]`);
    } else {
      this._logger.error(`Sort inventory column ${sortData.column.propertyKey} was not implemented`);
    }
    return httpParams;
  }
}

export interface RequiredParamsForAgentStepsActionsHttpRequests {
  integrationId: string;
  stepId: string;
}

export interface ActionGetAgentStepSensitiveDataResponseModel {
  resultCode: string;
  title: string;
  description?: string;
  payload?: string;
  transcript?: string;
  invocationResult?: Record<string, string>;
  invocationParams?: Record<string, Record<string, string>>;
  snippet?: Record<string, string>;
  singleSnippet?: string;
  ragQuery?: string;
  fileDownloadUrl?: string;
}

export enum SensitiveDataRequestActions {
  transcript = 'getTranscript',
  payload = 'getPayload',
  invocationResult = 'getInvocationResult',
  invocationParams = 'getInvocationParams',
  snippet = 'getSnippet',
  ragQuery = 'getRagQuery',
}
